<template>
  <div>
    <!-- File Upload Modal -->
    <div class="modal fade" id="uploadFile" tabindex="-1" role="dialog" aria-labelledby="exampleModalCenterTitle" aria-hidden="true">
      <div class="modal-dialog modal-dialog-centered" :class="{ 'modal-lg': showPreview }" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="exampleModalLongTitle">
              <i class="bx bx-upload mr-2"></i>Upload Transactions File
            </h5>
            <button @click="closeModal" type="button" class="close" data-dismiss="modal" aria-label="Close">
              <span aria-hidden="true">&times;</span>
            </button>
          </div>

          <div class="modal-body" v-if="!loading">
            <!-- Step Indicator -->
            <div class="steps-container mb-4">
              <div class="step-progress">
                <div class="step-progress-bar" :style="{ width: showPreview ? '100%' : '50%' }"></div>
              </div>
              <div class="steps">
                <div class="step active">
                  <div class="step-icon">
                    <i class="bx bx-file"></i>
                  </div>
                  <div class="step-label">Select File</div>
                </div>
                <div class="step" :class="{ active: showPreview }">
                  <div class="step-icon">
                    <i class="bx bx-table"></i>
                  </div>
                  <div class="step-label">Preview & Upload</div>
                </div>
              </div>
            </div>

            <!-- Stage 1: File Selection -->
            <div v-if="!showPreview">
              <div class="upload-container">
                <div class="upload-dropzone"
                     :class="{ 'is-dragover': isDragOver, 'has-file': selectedFile }"
                     @dragover.prevent="isDragOver = true"
                     @dragleave.prevent="isDragOver = false"
                     @drop.prevent="handleFileDrop($event)">

                  <input
                      id="fileInput"
                      name="file"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      class="file-input"
                      @change="handleFileChange($event)"
                  />

                  <div v-if="!selectedFile" class="upload-prompt">
                    <div class="upload-icon">
                      <i class="bx bxs-cloud-upload"></i>
                    </div>
                    <h4>Drag and drop your file here</h4>
                    <p class="text-muted">or</p>
                    <label for="fileInput" class="btn btn-primary btn-choose-file">
                      Choose File
                    </label>
                    <p class="mt-2 supported-files">Supported formats: CSV, XLSX, XLS</p>
                  </div>

                  <div v-else class="selected-file-container">
                    <div class="file-icon">
                      <i :class="getFileIcon(selectedFile.name)"></i>
                    </div>
                    <div class="file-details">
                      <h5 class="file-name">{{ selectedFile.name }}</h5>
                      <p class="file-size">{{ formatFileSize(selectedFile.size) }}</p>
                    </div>
                    <button class="btn btn-sm btn-outline-danger" @click="removeFile">
                      <i class="bx bx-trash"></i>
                    </button>
                  </div>
                </div>
              </div>

              <!-- Continue/Preview Button -->
              <div class="row mt-4">
                <div class="col-12">
                  <button
                      @click="previewData"
                      class="btn btn-primary btn-lg w-100"
                      :disabled="!selectedFile">
                    <i class="bx bx-table mr-1"></i> Preview Transactions
                  </button>
                </div>
              </div>
            </div>

            <!-- Stage 2: Data Preview -->
            <div v-if="showPreview">
              <div class="row">
                <div class="col-12">
                  <div class="d-flex justify-content-between align-items-center mb-3">
                    <h5>
                      <i class="bx bx-table mr-1"></i>
                      Transaction Preview
                      <span class="badge badge-pill badge-primary ml-2">{{ parsedData.length }} records</span>
                    </h5>
                    <button @click="backToUpload" class="btn btn-sm btn-outline-secondary">
                      <i class="bx bx-arrow-back mr-1"></i> Back
                    </button>
                  </div>

                  <!-- Date Range Selection -->
                  <div class="date-filter-container mb-4">
                    <div class="date-filter-header">
                      <i class="bx bx-calendar mr-1"></i>
                      <span class="fw-semibold">Select Date Range</span>
                    </div>
                    <div class="date-range-inputs">
                      <div class="date-input-group">
                        <label for="dateFrom">From</label>
                        <input
                            id="dateFrom"
                            v-model="dateFrom"
                            class="form-control"
                            placeholder="Start date"
                            type="date"
                        >
                      </div>
                      <div class="date-input-group">
                        <label for="dateTo">To</label>
                        <input
                            id="dateTo"
                            v-model="dateTo"
                            class="form-control"
                            placeholder="End date"
                            type="date"
                        >
                      </div>
                      <div class="date-range-actions">
                        <button
                            class="btn btn-sm btn-outline-primary"
                            @click="applyDateFilter"
                            :disabled="!dateFrom || !dateTo"
                        >
                          <i class="bx bx-filter mr-1"></i> Filter
                        </button>
                        <button
                            class="btn btn-sm btn-outline-secondary ml-2"
                            @click="resetDateFilter"
                            :disabled="!isDateFiltered"
                        >
                          <i class="bx bx-reset mr-1"></i> Reset
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- File Metadata Panel -->
                  <div v-if="showFileMetadata" class="metadata-container mb-4">
                    <div class="metadata-header">
                      <i class="bx bx-info-circle mr-1"></i>
                      <span>File Metadata</span>
                      <button class="btn btn-sm btn-link ml-auto" @click="toggleMetadata">
                        {{ showMetadataDetails ? 'Hide' : 'Show' }} Details
                      </button>
                    </div>
                    <div v-if="showMetadataDetails" class="metadata-content">
                      <div v-for="(line, index) in fileMetadata" :key="index" class="metadata-line">
                        {{ line }}
                      </div>
                      <div class="metadata-info">
                        <span class="badge badge-info">
                          <i class="bx bx-table mr-1"></i>Transaction headers found at row {{ headerRowIndex + 1 }}
                        </span>
                      </div>
                    </div>
                  </div>

                  <!-- Summary Stats -->
                  <div v-if="showStats" class="data-summary mb-4">
                    <div class="summary-item">
                      <div class="summary-icon">
                        <i class="bx bx-list-ul"></i>
                      </div>
                      <div class="summary-content">
                        <h6>Total Records</h6>
                        <div class="summary-value">{{ parsedData.length }}</div>
                      </div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-icon">
                        <i class="bx bx-calendar"></i>
                      </div>
                      <div class="summary-content">
                        <h6>Date Range</h6>
                        <div class="summary-value">{{ formatDisplayDate(dateFrom) }} to {{ formatDisplayDate(dateTo) }}</div>
                      </div>
                    </div>
                    <div class="summary-item">
                      <div class="summary-icon">
                        <i class="bx bx-file"></i>
                      </div>
                      <div class="summary-content">
                        <h6>File Name</h6>
                        <div class="summary-value">{{ selectedFile.name }}</div>
                      </div>
                    </div>
                  </div>

                  <!-- Search filter -->
                  <div class="mb-3 search-container">
                    <div class="input-group">
                      <div class="input-group-prepend">
                        <span class="input-group-text">
                          <i class="bx bx-search"></i>
                        </span>
                      </div>
                      <input
                          type="text"
                          class="form-control"
                          placeholder="Search transactions..."
                          v-model="searchQuery"
                          @input="handleSearch"
                      />
                      <div class="input-group-append" v-if="searchQuery">
                        <button class="btn btn-outline-secondary" type="button" @click="clearSearch">
                          <i class="bx bx-x"></i>
                        </button>
                      </div>
                    </div>
                  </div>

                  <!-- Data Table -->
                  <div class="table-container">
                    <table class="table table-striped table-bordered">
                      <thead class="table-dark sticky-header">
                      <tr>
                        <th
                            v-for="(header, index) in tableHeaders"
                            :key="index"
                            @click="sortTable(header)"
                            class="sortable-header"
                        >
                          {{ header }}
                          <i
                              v-if="sortKey === header"
                              class="ml-1"
                              :class="sortOrder === 'asc' ? 'bx bx-up-arrow-alt' : 'bx bx-down-arrow-alt'"
                          ></i>
                        </th>
                      </tr>
                      </thead>
                      <tbody>
                      <tr v-if="filteredData.length === 0">
                        <td :colspan="tableHeaders.length" class="text-center py-4">
                          <div class="empty-state">
                            <i class="bx bx-search"></i>
                            <p>No matching records found</p>
                            <button @click="clearFilters" class="btn btn-sm btn-outline-primary">
                              Clear Filters
                            </button>
                          </div>
                        </td>
                      </tr>
                      <tr v-for="(row, rowIndex) in displayedData" :key="rowIndex">
                        <td v-for="(header, colIndex) in tableHeaders" :key="colIndex">
                          {{ formatCellValue(row[header]) }}
                        </td>
                      </tr>
                      </tbody>
                    </table>
                  </div>

                  <!-- Pagination -->
                  <div class="pagination-container mt-3">
                    <div class="records-info">
                      Showing {{ displayedData.length ? (currentPage - 1) * itemsPerPage + 1 : 0 }}
                      to {{ Math.min(currentPage * itemsPerPage, filteredData.length) }}
                      of {{ filteredData.length }} records
                      <span v-if="isFiltered" class="filtered-text">
                        (filtered from {{ parsedData.length }} total records)
                      </span>
                    </div>
                    <div class="pagination-controls">
                      <div class="items-per-page mr-3">
                        <label for="itemsPerPage" class="mr-2">Show:</label>
                        <select id="itemsPerPage" v-model="itemsPerPage" class="form-control form-control-sm">
                          <option value="10">10</option>
                          <option value="25">25</option>
                          <option value="50">50</option>
                          <option value="100">100</option>
                        </select>
                      </div>
                      <button @click="firstPage" class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 1">
                        <i class="bx bx-chevrons-left"></i>
                      </button>
                      <button @click="prevPage" class="btn btn-sm btn-outline-secondary" :disabled="currentPage === 1">
                        <i class="bx bx-chevron-left"></i>
                      </button>
                      <span class="page-info">
                        <input
                            type="number"
                            v-model.number="currentPage"
                            class="form-control form-control-sm page-input"
                            min="1"
                            :max="totalPages"
                            @change="validatePageNumber"
                        >
                        <span>of {{ totalPages }}</span>
                      </span>
                      <button @click="nextPage" class="btn btn-sm btn-outline-secondary" :disabled="currentPage === totalPages">
                        <i class="bx bx-chevron-right"></i>
                      </button>
                      <button @click="lastPage" class="btn btn-sm btn-outline-secondary" :disabled="currentPage === totalPages">
                        <i class="bx bx-chevrons-right"></i>
                      </button>
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <div class="mt-4 d-flex justify-content-end">
                    <button @click="backToUpload" class="btn btn-outline-secondary mr-2">
                      <i class="bx bx-revision mr-1"></i> Change File
                    </button>
                    <button @click="uploadDataToAPI" class="btn btn-success">
                      <i class="bx bx-cloud-upload mr-1"></i> Upload Transactions
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Alerts -->
            <div v-if="message !== null" class="alert-container mt-3">
              <div v-if="alert.failed" class="alert alert-danger fade show" role="alert">
                <div class="d-flex align-items-center">
                  <i class="bx bx-error-circle mr-2"></i>
                  <div>
                    <strong>Error!</strong> {{ message }}
                  </div>
                  <button type="button" class="close ml-auto" @click="resetAlerts">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
              <div v-if="alert.success" class="alert alert-success fade show" role="alert">
                <div class="d-flex align-items-center">
                  <i class="bx bx-check-circle mr-2"></i>
                  <div>
                    <strong>Success!</strong> {{ message }}
                  </div>
                  <button type="button" class="close ml-auto" @click="resetAlerts">
                    <span aria-hidden="true">&times;</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          <!-- Loading State -->
          <div v-if="loading" class="modal-body">
            <div class="loading-container">
              <div class="spinner">
                <div v-if="!isPaused" class="spinner-border text-primary" role="status">
                  <span class="sr-only">Loading...</span>
                </div>
                <div v-else class="pause-icon-wrapper">
                  <i class="bx bx-pause-circle text-warning" style="font-size: 3rem;"></i>
                </div>
              </div>
              <p class="loading-message">{{ loadingMessage }}</p>

              <!-- Progress Bar -->
              <div v-if="uploadProgress > 0" class="w-100 mt-3">
                <div class="progress" style="height: 22px;">
                  <div
                      class="progress-bar"
                      :class="progressBarClass"
                      role="progressbar"
                      :style="{ width: uploadProgress + '%' }"
                      :aria-valuenow="uploadProgress"
                      aria-valuemin="0"
                      aria-valuemax="100"
                  >
                    {{ Math.round(uploadProgress) }}%
                  </div>
                </div>
              </div>

              <!-- Batch Stats -->
              <div v-if="batchTotal > 0" class="batch-stats mt-3 w-100">
                <div class="d-flex justify-content-between text-muted small mb-1">
                  <span>Batch {{ batchCurrent }} of {{ batchTotal }}</span>
                  <span>{{ batchRecordsProcessed }} / {{ batchRecordsTotal }} records</span>
                </div>
                <div class="d-flex justify-content-between text-muted small">
                  <span><i class="bx bx-time-five mr-1"></i>Elapsed: {{ batchElapsed }}</span>
                  <span v-if="batchEta && !batchFailed && !isPaused"><i class="bx bx-hourglass mr-1"></i>ETA: {{ batchEta }}</span>
                  <span v-if="isPaused" class="text-warning"><i class="bx bx-pause mr-1"></i>Paused</span>
                  <span v-if="batchFailed" class="text-danger"><i class="bx bx-error mr-1"></i>Failed</span>
                </div>
              </div>

              <!-- Pause / Resume / Cancel Buttons -->
              <div v-if="batchTotal > 0 && !batchFailed && uploadProgress < 95" class="batch-controls mt-3 d-flex justify-content-center" style="gap: 0.5rem;">
                <button
                    v-if="!isPaused"
                    @click="pauseUpload"
                    class="btn btn-warning btn-sm"
                >
                  <i class="bx bx-pause mr-1"></i> Pause
                </button>
                <button
                    v-if="isPaused"
                    @click="resumeUpload"
                    class="btn btn-success btn-sm"
                >
                  <i class="bx bx-play mr-1"></i> Resume
                </button>
                <button
                    @click="cancelUpload"
                    class="btn btn-outline-danger btn-sm"
                >
                  <i class="bx bx-stop mr-1"></i> Cancel
                </button>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import * as XLSX from 'xlsx';
import Papa from 'papaparse';
import { execute, execute2, biller, paysol } from "@/api";

export default {
  name: 'FileUploadPreview',
  data() {
    return {
      // File Selection
      shortCode: '',
      formatType: '',
      bankType: '',
      bankID: '',
      selectedFile: null,
      isDragOver: false,

      // UI States
      showPreview: false,
      loading: false,
      loadingMessage: 'Processing your file. Please wait...',
      uploadProgress: 0,
      showStats: true,

      // File Metadata
      fileMetadata: [],
      showFileMetadata: false,
      showMetadataDetails: false,
      headerRowIndex: 0,

      // Data & Pagination
      parsedData: [],
      tableHeaders: [],
      currentPage: 1,
      itemsPerPage: 10,
      backupData: [], // For restoring after filters

      // Sorting & Filtering
      sortKey: '',
      sortOrder: 'asc',
      searchQuery: '',
      searchTimeout: null,
      isDateFiltered: false,

      // Alerts
      message: null,
      alert: {
        success: false,
        info: false,
        failed: false
      },

      // Date Range
      dateTo: '',
      dateFrom: '',
      originalDateFrom: '',
      originalDateTo: '',

      // Batch Upload Tracking
      batchSize: 10,
      batchCurrent: 0,
      batchTotal: 0,
      batchRecordsProcessed: 0,
      batchRecordsTotal: 0,
      batchStartTime: null,
      batchElapsed: '',
      batchEta: '',
      batchTimerInterval: null,
      batchFailed: false,

      // Pause / Resume / Cancel
      isPaused: false,
      isCancelled: false,
      pauseResolver: null,       // resolves the pause promise when resumed
      pauseActiveTime: 0,        // total ms spent actively uploading (excludes paused time)
      pausedAtTime: null,         // timestamp when pause started
    };
  },
  computed: {
    isFiltered() {
      return this.searchQuery || this.isDateFiltered;
    },
    filteredData() {
      let data = [...this.parsedData];

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        data = data.filter(row => {
          return Object.values(row).some(value => {
            if (value === null || value === undefined) return false;
            return String(value).toLowerCase().includes(query);
          });
        });
      }

      // Apply date filter
      if (this.isDateFiltered && this.dateFrom && this.dateTo) {
        const dateFields = this.findDateFields();
        if (dateFields.length > 0) {
          const dateField = dateFields[0];
          const fromDate = new Date(this.dateFrom);
          const toDate = new Date(this.dateTo);
          toDate.setHours(23, 59, 59, 999); // Include the entire day

          data = data.filter(row => {
            const rowDate = this.parseDate(row[dateField]);
            return rowDate >= fromDate && rowDate <= toDate;
          });
        }
      }

      // Apply sorting
      if (this.sortKey) {
        data.sort((a, b) => {
          let valA = a[this.sortKey];
          let valB = b[this.sortKey];

          // Handle null/undefined values
          if (valA === null || valA === undefined) return this.sortOrder === 'asc' ? -1 : 1;
          if (valB === null || valB === undefined) return this.sortOrder === 'asc' ? 1 : -1;

          // Check if values are dates
          const dateA = this.isDate(valA) ? new Date(valA) : null;
          const dateB = this.isDate(valB) ? new Date(valB) : null;

          if (dateA && dateB) {
            return this.sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
          }

          // Check if values are numbers
          if (!isNaN(valA) && !isNaN(valB)) {
            return this.sortOrder === 'asc' ? valA - valB : valB - valA;
          }

          // Default string comparison
          valA = String(valA).toLowerCase();
          valB = String(valB).toLowerCase();

          if (valA < valB) return this.sortOrder === 'asc' ? -1 : 1;
          if (valA > valB) return this.sortOrder === 'asc' ? 1 : -1;
          return 0;
        });
      }

      return data;
    },
    totalPages() {
      return Math.ceil(this.filteredData.length / this.itemsPerPage);
    },
    displayedData() {
      const start = (this.currentPage - 1) * this.itemsPerPage;
      const end = start + this.itemsPerPage;
      return this.filteredData.slice(start, end);
    },
    progressBarClass() {
      if (this.batchFailed) return 'bg-danger';
      if (this.isPaused) return 'bg-warning progress-bar-striped';
      return 'bg-primary progress-bar-striped progress-bar-animated';
    }
  },
  methods: {
    // STEP 1: File Selection Methods
    handleFileChange(event) {
      this.selectedFile = event.target.files[0];
      this.resetAlerts();
    },

    handleFileDrop(event) {
      this.isDragOver = false;
      const file = event.dataTransfer.files[0];
      if (file) {
        const extension = file.name.split('.').pop().toLowerCase();
        if (['csv', 'xlsx', 'xls'].includes(extension)) {
          this.selectedFile = file;
          this.resetAlerts();
        } else {
          this.showError('Please upload a CSV or Excel file.');
        }
      }
    },

    removeFile() {
      this.selectedFile = null;
      document.getElementById('fileInput').value = '';
    },

    getFileIcon(fileName) {
      const extension = fileName.split('.').pop().toLowerCase();
      switch (extension) {
        case 'csv':
          return 'bx bxs-file-csv';
        case 'xlsx':
        case 'xls':
          return 'bx bxs-spreadsheet';
        default:
          return 'bx bxs-file';
      }
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    // STEP 2: Preview Data Methods
    previewData() {
      if (!this.selectedFile) {
        this.showError('Please select a file before continuing.');
        return;
      }

      this.loading = true;
      this.loadingMessage = 'Processing file for preview...';
      this.uploadProgress = 10;

      const fileType = this.selectedFile.name.split('.').pop().toLowerCase();
      const reader = new FileReader();

      reader.onload = (e) => {
        try {
          this.uploadProgress = 40;
          if (fileType === 'csv') {
            this.parseCSV(e.target.result);
          } else if (['xlsx', 'xls'].includes(fileType)) {
            this.parseExcel(e.target.result);
          } else {
            this.showError('Unsupported file format. Please upload a CSV or Excel file.');
          }
          this.uploadProgress = 0;
        } catch (error) {
          this.showError('Error processing the file: ' + error.message);
          this.uploadProgress = 0;
        }
      };

      reader.onerror = () => {
        this.showError('Error reading the file.');
        this.uploadProgress = 0;
      };

      if (fileType === 'csv') {
        reader.readAsText(this.selectedFile);
      } else {
        reader.readAsArrayBuffer(this.selectedFile);
      }
    },

    // Helper for splitting CSV lines properly (handles quoted fields)
    splitCSVLine(line) {
      // Use Papa Parse to correctly handle quoted fields
      return Papa.parse(line, {
        delimiter: ',',
        skipEmptyLines: true
      }).data[0] || [];
    },

    // Store metadata from rows above the header
    extractMetadata(metadataRows) {
      if (!metadataRows || metadataRows.length === 0) return;

      this.fileMetadata = metadataRows.map(row => {
        if (Array.isArray(row)) {
          return row.filter(cell => cell !== null && cell !== undefined && cell !== '').join(': ');
        } else {
          return row;
        }
      }).filter(line => line.trim() !== '');

      // Add metadata to data for display
      this.showFileMetadata = this.fileMetadata.length > 0;
      this.showMetadataDetails = true; // Show by default

      // Extract the Short Code from the metadata
      this.extractShortCode();
    },

    extractShortCode() {
      if (!this.fileMetadata || this.fileMetadata.length === 0) {
        console.log('No metadata available to extract Short Code');
        return '';
      }

      const metadataText = this.fileMetadata.join(' ');

      // Fixed regex to handle CSV format: "Short Code:,434343" or "Short Code: 434343"
      const shortCodeMatch = metadataText.match(/Short\s*Code\s*[:;,]+\s*(\d+)/i);

      if (shortCodeMatch && shortCodeMatch[1]) {
        this.shortCode = shortCodeMatch[1].trim();
        console.log(`Extracted Short Code: ${this.shortCode}`);
        return this.shortCode;
      } else {
        // Fallback: try to extract from filename (e.g., "ORG_0434343_")
        if (this.selectedFile && this.selectedFile.name) {
          const filenameMatch = this.selectedFile.name.match(/ORG[_-]?(\d+)/i);
          if (filenameMatch && filenameMatch[1]) {
            this.shortCode = filenameMatch[1].trim();
            console.log(`Extracted Short Code from filename: ${this.shortCode}`);
            return this.shortCode;
          }
        }
        console.log('Short Code not found in metadata');
        return '';
      }
    },


    processParseResults(data, headers) {
      // Filter out any problematic headers and check for empty data
      const cleanedHeaders = headers.filter(h => h !== null && h !== undefined && h.trim() !== '');

      if (cleanedHeaders.length === 0) {
        this.showError('No valid headers found in the file.');
        this.loading = false;
        return;
      }

      // Store the parsed data and headers
      this.parsedData = data;
      this.backupData = [...data];
      this.tableHeaders = cleanedHeaders;

      // Set initial date range based on data
      this.setInitialDateRange();

      // Show the preview
      this.showPreview = true;
      this.loading = false;

      // Reset pagination
      this.currentPage = 1;
    },

    // Date Range Methods
    setInitialDateRange() {
      const dateFields = this.findDateFields();

      if (dateFields.length > 0) {
        const dateField = dateFields[0];
        let minDate = null;
        let maxDate = null;

        // Find min and max dates in the data
        this.parsedData.forEach(row => {
          const rowDate = this.parseDate(row[dateField]);
          if (rowDate) {
            if (!minDate || rowDate < minDate) minDate = rowDate;
            if (!maxDate || rowDate > maxDate) maxDate = rowDate;
          }
        });

        if (minDate && maxDate) {
          this.dateFrom = this.formatDateForInput(minDate);
          this.dateTo = this.formatDateForInput(maxDate);
          this.originalDateFrom = this.dateFrom;
          this.originalDateTo = this.dateTo;
        }
      }
    },

    findDateFields() {
      const datePatterns = [
        /date/i, /time/i, /created/i, /completion/i, /initiation/i, /timestamp/i
      ];

      return this.tableHeaders.filter(header => {
        // Check if header name contains date-related words
        if (datePatterns.some(pattern => pattern.test(header))) {
          return true;
        }

        // Check if values in this column are dates
        const hasDateValues = this.parsedData.some(row => {
          const value = row[header];
          return value && this.isDate(value);
        });

        return hasDateValues;
      });
    },

    parseDate(value) {
      if (!value) return null;

      // Handle different date formats
      if (value instanceof Date) return value;

      // Handle Excel numeric dates
      if (typeof value === 'number' && !isNaN(value)) {
        // Excel dates are number of days since 1900-01-01
        const date = new Date(1900, 0, 1);
        date.setDate(date.getDate() + value - 2); // Adjust for Excel's date bug
        return date;
      }

      // Handle string dates
      if (typeof value === 'string') {
        // Try standard date parsing
        const date = new Date(value);
        if (!isNaN(date.getTime())) return date;

        // Try common formats like DD/MM/YYYY or MM/DD/YYYY
        const formats = [
          // DD/MM/YYYY
          { regex: /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/,
            fn: (m) => new Date(`${m[3].length === 2 ? '20' + m[3] : m[3]}-${m[2]}-${m[1]}`) },
          // MM/DD/YYYY
          { regex: /^(\d{1,2})[\/-](\d{1,2})[\/-](\d{2,4})$/,
            fn: (m) => new Date(`${m[3].length === 2 ? '20' + m[3] : m[3]}-${m[1]}-${m[2]}`) },
          // YYYY-MM-DD
          { regex: /^(\d{4})[\/-](\d{1,2})[\/-](\d{1,2})$/,
            fn: (m) => new Date(`${m[1]}-${m[2]}-${m[3]}`) }
        ];

        for (const format of formats) {
          const match = value.match(format.regex);
          if (match) {
            const date = format.fn(match);
            if (!isNaN(date.getTime())) return date;
          }
        }
      }

      return null;
    },

    isDate(value) {
      return this.parseDate(value) !== null;
    },

    formatDateForInput(date) {
      if (!date) return '';

      // Format as YYYY-MM-DD for input[type="date"]
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');

      return `${year}-${month}-${day}`;
    },

    formatDisplayDate(dateStr) {
      if (!dateStr) return 'N/A';

      const date = new Date(dateStr);
      if (isNaN(date.getTime())) return dateStr;

      // Format as DD/MM/YYYY for display
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();

      return `${day}/${month}/${year}`;
    },

    // Table Filter & Sort Methods
    applyDateFilter() {
      if (!this.dateFrom || !this.dateTo) {
        this.showError('Please select both start and end dates.');
        return;
      }

      const fromDate = new Date(this.dateFrom);
      const toDate = new Date(this.dateTo);

      if (fromDate > toDate) {
        this.showError('Start date must be before end date.');
        return;
      }

      this.isDateFiltered = true;
      this.currentPage = 1;
    },

    resetDateFilter() {
      this.dateFrom = this.originalDateFrom;
      this.dateTo = this.originalDateTo;
      this.isDateFiltered = false;
      this.currentPage = 1;
    },

    sortTable(header) {
      if (this.sortKey === header) {
        this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortKey = header;
        this.sortOrder = 'asc';
      }
    },

    handleSearch() {
      // Debounce search input
      clearTimeout(this.searchTimeout);
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1;
      }, 300);
    },

    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },

    clearFilters() {
      this.searchQuery = '';
      this.resetDateFilter();
      this.sortKey = '';
      this.sortOrder = 'asc';
      this.currentPage = 1;
    },

    // Pagination Methods
    nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
      }
    },

    prevPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
      }
    },

    firstPage() {
      this.currentPage = 1;
    },

    lastPage() {
      this.currentPage = this.totalPages;
    },

    validatePageNumber() {
      if (this.currentPage < 1) {
        this.currentPage = 1;
      } else if (this.currentPage > this.totalPages) {
        this.currentPage = this.totalPages;
      }
    },

    // UI Helpers
    formatCellValue(value, header) {
      if (value === null || value === undefined) return '';

      // Handle dates in format "01/01/400" or "01/01/1950"
      if (typeof value === 'string' && value.match(/^\d{1,2}\/\d{1,2}\/\d+$/)) {
        // This looks like a date - check if it's a valid date
        const parts = value.split('/');
        const day = parseInt(parts[0], 10);
        const month = parseInt(parts[1], 10);
        const year = parseInt(parts[2], 10);

        // Basic date validation
        if (day >= 1 && day <= 31 && month >= 1 && month <= 12) {
          // This is likely a date, not an amount
          if (this.isDate(value)) {
            const date = this.parseDate(value);
            return this.formatDisplayDate(date.toISOString());
          }
          // If isDate fails but it still looks like a date, just return the original
          return value;
        }
      }

      // For the "Paid In" column, ensure it's treated as currency
      if (header === 'Paid In' ||
          (typeof header === 'string' && (
              header.toLowerCase().includes('paid') ||
              header.toLowerCase().includes('amount')
          ))) {

        // If it's already a number, format it as currency
        if (typeof value === 'number') {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }

        // If it's a string that might contain a numeric amount
        if (typeof value === 'string') {
          // Only treat as amount if it clearly has currency markers or doesn't look like a date
          if (value.includes('$') || value.includes('£') ||
              value.includes('€') || value.includes('Ksh') ||
              !value.includes('/')) {

            // Try to extract just the numeric part
            const numericMatch = value.match(/(\d[\d,]*)(?:\.\d+)?$/);
            if (numericMatch) {
              const amount = parseFloat(numericMatch[0].replace(/,/g, ''));
              if (!isNaN(amount)) {
                return amount.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                });
              }
            }
          }
        }
      }

      // Existing number formatting code
      if (typeof value === 'number') {
        // Check if it might be currency
        const mightBeCurrency = this.parsedData.some(row => {
          const rowValue = row[this.tableHeaders.find(h => row[h] === value)];
          return rowValue && typeof rowValue === 'string' &&
              (rowValue.includes('$') || rowValue.includes('£') ||
                  rowValue.includes('€') || rowValue.includes('Ksh'));
        });

        if (mightBeCurrency) {
          return value.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
          });
        }

        return value.toLocaleString();
      }

      return value;
    },

    toggleMetadata() {
      this.showMetadataDetails = !this.showMetadataDetails;
    },

    // Navigation Methods
    backToUpload() {
      this.showPreview = false;
      this.clearFilters();
    },

    closeModal() {
      this.$emit('close');
      this.resetComponent();
    },

    resetComponent() {
      this.selectedFile = null;
      this.showPreview = false;
      this.parsedData = [];
      this.tableHeaders = [];
      this.currentPage = 1;
      this.resetAlerts();
      document.getElementById('fileInput').value = '';
    },

    // Alert Methods
    showError(message) {
      this.message = message;
      this.alert.failed = true;
      this.alert.success = false;
      this.loading = false;
    },

    showSuccess(message) {
      this.message = message;
      this.alert.failed = false;
      this.alert.success = true;
    },

    resetAlerts() {
      this.message = null;
      this.alert.failed = false;
      this.alert.success = false;
    },


    // API Integration
    uploadDataToAPI2() {
      if (this.parsedData.length === 0) {
        this.showError('No data to upload.');
        return;
      }

      this.loading = true;
      this.loadingMessage = 'Uploading transactions...';
      this.uploadProgress = 25;

      // Prepare data for upload
      const dataToUpload = {
        transactions: this.formatDataForUpload(),
        fileMetadata: this.fileMetadata,
        fileName: this.selectedFile.name,
        headers: this.tableHeaders,
        bankType: this.detectBankType()
      };

      // Simulate API call or replace with actual API call
    },

    uploadDataToAPI() {
      if (this.parsedData.length === 0) {
        this.showError('No data to upload. Please select a valid file.');
        return;
      }

      // Filter data if needed
      let dataToUpload = this.parsedData;
      if (this.isDateFiltered && this.filteredData.length < this.parsedData.length) {
        const confirmUpload = confirm(`You are about to upload ${this.filteredData.length} filtered records out of ${this.parsedData.length} total records. Continue?`);
        if (!confirmUpload) return;
        dataToUpload = this.filteredData;
      }

      // Reset batch tracking state
      this.loading = true;
      this.batchFailed = false;
      this.isPaused = false;
      this.isCancelled = false;
      this.pauseResolver = null;
      this.pauseActiveTime = 0;
      this.pausedAtTime = null;
      this.batchRecordsTotal = dataToUpload.length;
      this.batchRecordsProcessed = 0;
      this.batchStartTime = Date.now();
      this.batchElapsed = '0s';
      this.batchEta = 'calculating...';
      this.uploadProgress = 0;
      this.loadingMessage = 'Initializing batch upload...';

      // Start elapsed time timer
      this.batchTimerInterval = setInterval(() => {
        this.batchElapsed = this.formatDuration(Date.now() - this.batchStartTime);
      }, 1000);

      // Split data into chunks
      const chunks = [];
      for (let i = 0; i < dataToUpload.length; i += this.batchSize) {
        chunks.push(dataToUpload.slice(i, i + this.batchSize));
      }
      this.batchTotal = chunks.length;
      this.batchCurrent = 0;

      // Step 1: Initialize — upload file + create report row
      const initData = new FormData();
      initData.append("function", "uploadBankToQueBatchInit");
      initData.append("fileToUpload", this.selectedFile);
      initData.append("dateFrom", this.dateFrom);
      initData.append("dateTo", this.dateTo);
      initData.append("totalRecords", dataToUpload.length.toString());
      initData.append("sessionNames", sessionStorage.getItem("sessionNames"));
      initData.append("sessionIdNo", sessionStorage.getItem("sessionIdNo"));
      initData.append("sampleRow", JSON.stringify(dataToUpload[0]));
      if (this.fileMetadata) {
        initData.append("fileMetadata", this.fileMetadata);
      }
      if (this.shortCode) {
        initData.append("shortCode", this.shortCode);
      }

      const stream = biller;

      execute2(initData, stream)
          .then((res) => {
            const initResult = res.data;
            if (!initResult.success) {
              throw new Error(initResult.message || 'Failed to initialize batch upload.');
            }

            const reconCode = initResult.reconCode;
            const formatType = initResult.formatType;
            const shortCode = initResult.shortCode;

            this.uploadProgress = 5;
            this.loadingMessage = `Uploading ${dataToUpload.length} records in ${chunks.length} batches...`;

            // Step 2: Send chunks sequentially
            return this.sendBatchesSequentially(chunks, reconCode, formatType, shortCode, stream);
          })
          .then((result) => {
            // Step 3: Finalize
            this.loadingMessage = 'Finalizing and computing summary...';
            this.uploadProgress = 95;

            const completeData = new FormData();
            completeData.append("function", "uploadBankToQueBatchComplete");
            completeData.append("reconCode", result.reconCode);
            completeData.append("dateFrom", this.dateFrom);
            completeData.append("dateTo", this.dateTo);

            return execute2(completeData, stream);
          })
          .then((res) => {
            this.stopBatchTimer();
            this.uploadProgress = 100;
            this.loadingMessage = 'Upload complete!';
            this.batchEta = 'Done';

            setTimeout(() => {
              this.loading = false;
              this.uploadProgress = 0;
              this.resetBatchState();

              if (res.data.success) {
                this.showSuccess(res.data.message);
                this.$emit('upload-complete');
                this.showPreview = false;
                this.$emit('refresh-uploads');
                setTimeout(() => { this.closeModal(); }, 2000);
              } else {
                this.showError(res.data.message || 'Upload completed but finalization failed.');
              }
            }, 500);
          })
          .catch((e) => {
            this.stopBatchTimer();
            this.batchFailed = true;
            this.loadingMessage = 'Upload failed: ' + (e.message || 'Unknown error');
            this.batchEta = '';

            setTimeout(() => {
              this.loading = false;
              this.uploadProgress = 0;
              this.resetBatchState();
              this.showError('Upload failed: ' + (e.message || 'Network error'));
            }, 2500);
          });
    },

    /**
     * Sends an array of chunks to the batch endpoint one at a time.
     * Checks for pause/cancel between each batch.
     * Returns a promise that resolves with { reconCode } when all are done.
     */
    async sendBatchesSequentially(chunks, reconCode, formatType, shortCode, stream) {
      for (let i = 0; i < chunks.length; i++) {

        // --- Check if cancelled ---
        if (this.isCancelled) {
          throw new Error('Upload cancelled by user.');
        }

        // --- Check if paused — wait until resumed ---
        if (this.isPaused) {
          this.loadingMessage = `Paused at batch ${this.batchCurrent} of ${chunks.length}. Click Resume to continue.`;
          await new Promise((resolve) => {
            this.pauseResolver = resolve;
          });
          // After resume, re-check cancel
          if (this.isCancelled) {
            throw new Error('Upload cancelled by user.');
          }
          this.loadingMessage = `Resuming upload...`;
        }

        this.batchCurrent = i + 1;

        const batchData = new FormData();
        batchData.append("function", "uploadBankToQueBatch");
        batchData.append("reconCode", reconCode);
        batchData.append("formatType", formatType);
        batchData.append("shortCode", shortCode);
        batchData.append("jsonData", JSON.stringify(chunks[i]));

        if (formatType === 'MPESA' && this.shortCode) {
          batchData.append("mpesaShortCode", this.shortCode);
        }

        const res = await execute2(batchData, stream);

        if (!res.data.success) {
          throw new Error(res.data.message || `Batch ${i + 1} failed.`);
        }

        // Update progress
        this.batchRecordsProcessed += chunks[i].length;
        // Progress range: 5% (init done) to 95% (before finalize)
        this.uploadProgress = 5 + ((this.batchCurrent / chunks.length) * 90);
        this.loadingMessage = `Batch ${this.batchCurrent} of ${chunks.length} complete...`;

        // Calculate ETA using active time only (excludes paused time)
        const activeTime = this.getActiveElapsed();
        const avgPerBatch = activeTime / this.batchCurrent;
        const remaining = avgPerBatch * (chunks.length - this.batchCurrent);
        this.batchEta = remaining > 0 ? this.formatDuration(remaining) : 'almost done...';
      }

      return { reconCode };
    },

    // -------- Pause / Resume / Cancel --------

    pauseUpload() {
      this.isPaused = true;
      this.pausedAtTime = Date.now();
      this.loadingMessage = `Paused at batch ${this.batchCurrent} of ${this.batchTotal}. Click Resume to continue.`;
    },

    resumeUpload() {
      if (!this.isPaused) return;

      // Track how long we were paused so ETA stays accurate
      if (this.pausedAtTime) {
        this.pauseActiveTime += (Date.now() - this.pausedAtTime);
        this.pausedAtTime = null;
      }

      this.isPaused = false;
      this.loadingMessage = `Resuming upload...`;

      // Release the waiting promise in sendBatchesSequentially
      if (this.pauseResolver) {
        this.pauseResolver();
        this.pauseResolver = null;
      }
    },

    cancelUpload() {
      const confirmCancel = confirm(
          `Are you sure you want to cancel? ${this.batchRecordsProcessed} of ${this.batchRecordsTotal} records have already been uploaded and processed on the server.`
      );
      if (!confirmCancel) return;

      this.isCancelled = true;
      this.isPaused = false;

      // If we were paused and waiting, release the promise so the loop can exit
      if (this.pauseResolver) {
        this.pauseResolver();
        this.pauseResolver = null;
      }
    },

    /**
     * Returns the total elapsed time minus any time spent paused.
     */
    getActiveElapsed() {
      const totalElapsed = Date.now() - this.batchStartTime;
      let pausedTime = this.pauseActiveTime;
      // If currently paused, include the ongoing pause duration
      if (this.isPaused && this.pausedAtTime) {
        pausedTime += (Date.now() - this.pausedAtTime);
      }
      return totalElapsed - pausedTime;
    },

    /**
     * Format milliseconds into a human-readable duration string.
     */
    formatDuration(ms) {
      const totalSeconds = Math.floor(ms / 1000);
      if (totalSeconds < 60) return `${totalSeconds}s`;
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      if (minutes < 60) return `${minutes}m ${seconds}s`;
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      return `${hours}h ${mins}m`;
    },

    stopBatchTimer() {
      if (this.batchTimerInterval) {
        clearInterval(this.batchTimerInterval);
        this.batchTimerInterval = null;
      }
    },

    resetBatchState() {
      this.batchCurrent = 0;
      this.batchTotal = 0;
      this.batchRecordsProcessed = 0;
      this.batchRecordsTotal = 0;
      this.batchStartTime = null;
      this.batchElapsed = '';
      this.batchEta = '';
      this.batchFailed = false;
      this.isPaused = false;
      this.isCancelled = false;
      this.pauseResolver = null;
      this.pauseActiveTime = 0;
      this.pausedAtTime = null;
    },


    formatDataForUpload() {
      // Basic formatting - can be expanded based on API requirements
      return this.parsedData.map(row => {
        const formattedRow = { ...row };

        // Format dates for API
        this.findDateFields().forEach(dateField => {
          if (row[dateField]) {
            const date = this.parseDate(row[dateField]);
            if (date) {
              formattedRow[dateField] = date.toISOString();
            }
          }
        });

        return formattedRow;
      });
    },

    detectBankType() {
      // Auto-detect bank type based on file content and patterns
      const fileContent = this.selectedFile.name.toLowerCase();
      const metadataText = this.fileMetadata.join(' ').toLowerCase();
      const headerText = this.tableHeaders.join(' ').toLowerCase();

      // Check for common bank patterns
      if (fileContent.includes('mpesa') ||
          metadataText.includes('mpesa') ||
          headerText.includes('mpesa')) {
        return 'mpesa';
      }

      if (fileContent.includes('equity') ||
          metadataText.includes('equity') ||
          headerText.includes('equity')) {
        return 'equity';
      }

      if (fileContent.includes('kcb') ||
          metadataText.includes('kcb') ||
          headerText.includes('kcb')) {
        return 'kcb';
      }

      // Default value if no specific bank is detected
      return 'generic';
    },

    // Enhanced detectHeaderRow method for CSV files
    detectHeaderRow(lines) {
      // Skip empty lines
      lines = lines.filter(line => line.trim() !== '');

      if (lines.length < 2) return 0; // Not enough data

      // More comprehensive header patterns to look for
      const headerPatterns = [
        /receipt.?no/i, /transaction.?id/i, /date/i, /time/i,
        /amount/i, /balance/i, /reference/i, /description/i,
        /account/i, /payment/i, /withdrawal/i, /deposit/i,
        /paid.?in/i, /withdrawn/i, /status/i, /details/i,
        /completion/i, /initiation/i, /party/i, /reason/i,
        /narration/i, /mpesa/i, /message/i, /sender/i, /recipient/i,
        /debit/i, /credit/i, /transfer/i, /bank/i, /branch/i,
        /remarks/i, /type/i, /category/i, /currency/i, /fee/i,
        /statement/i, /particulars/i, /charges/i, /serial/i
      ];

      // Statistical analysis approach
      const columnTypeConsistency = [];

      // Check first 10 rows for content type consistency
      for (let i = 0; i < Math.min(lines.length, 20); i++) {
        const row = this.splitCSVLine(lines[i]);

        // Skip rows with too few columns
        if (row.length < 3) continue;

        // Track potential score for this row being a header
        let headerScore = 0;

        // Check if columns match header patterns
        const headerPatternMatches = row.filter(cell =>
            headerPatterns.some(pattern => pattern.test(cell))
        ).length;

        headerScore += headerPatternMatches * 2;

        // Check for text vs numeric content
        const textColumns = row.filter(cell => isNaN(cell.trim()) && cell.trim() !== '').length;
        const numericColumns = row.filter(cell => !isNaN(cell.trim()) && cell.trim() !== '').length;

        // Headers typically have more text than numbers
        if (textColumns > numericColumns) {
          headerScore += 1;
        }

        // Check for typical header formatting (title case, no punctuation in middle)
        const titleCaseCount = row.filter(cell => {
          const words = cell.trim().split(/\s+/);
          return words.length > 0 && words.some(word =>
              word.length > 0 &&
              word[0] === word[0].toUpperCase() &&
              word.slice(1).toLowerCase() === word.slice(1)
          );
        }).length;

        headerScore += titleCaseCount / row.length;

        // Store the score for this row
        columnTypeConsistency.push({
          index: i,
          score: headerScore,
          columnCount: row.length,
        });
      }

      // Sort the rows by score (highest first)
      columnTypeConsistency.sort((a, b) => b.score - a.score);

      // If we have a clear winner with a good score (at least 3 points)
      if (columnTypeConsistency.length > 0 && columnTypeConsistency[0].score >= 3) {
        const candidateHeaderRow = columnTypeConsistency[0].index;

        // Verify that data rows follow the same structure
        if (candidateHeaderRow < lines.length - 1) {
          const headerColumns = this.splitCSVLine(lines[candidateHeaderRow]);
          const dataRowsWithSameStructure = lines.slice(candidateHeaderRow + 1, candidateHeaderRow + 5)
              .filter(line => {
                const columns = this.splitCSVLine(line);
                return columns.length === headerColumns.length;
              }).length;

          // If at least 2 following rows have the same structure, this is likely the header
          if (dataRowsWithSameStructure >= 1) {
            return candidateHeaderRow;
          }
        }
      }

      // Fallback: Use more traditional approaches

      // Check for common metadata patterns before transaction data starts
      for (let i = 0; i < Math.min(lines.length - 1, 15); i++) {
        const currentLine = lines[i].toLowerCase();
        const nextLine = lines[i + 1].toLowerCase();

        // Check for transition markers (often appear right before headers)
        if (
            currentLine.includes('transaction details') ||
            currentLine.includes('statement details') ||
            currentLine.includes('transaction history') ||
            currentLine.includes('----') ||
            currentLine.includes('====')
        ) {
          return i + 1; // Header is usually the next line
        }

        // Check for metadata-to-header transition patterns
        // Often metadata has key-value pairs, while headers are single words
        const currentParts = this.splitCSVLine(currentLine);
        const nextParts = this.splitCSVLine(nextLine);

        const currentHasKeyValue = currentParts.some(p => p.includes(':'));
        const nextHasHeaders = nextParts.some(p =>
            headerPatterns.some(pattern => pattern.test(p)) && !p.includes(':')
        );

        if (currentHasKeyValue && nextHasHeaders) {
          return i + 1;
        }
      }

      // Last fallback: look for the row with the most header-like terms
      const headerScores = lines.slice(0, Math.min(lines.length, 20)).map((line, index) => {
        const columns = this.splitCSVLine(line);
        const headerHits = columns.filter(col =>
            headerPatterns.some(pattern => pattern.test(col))
        ).length;
        return { index, score: headerHits };
      });

      headerScores.sort((a, b) => b.score - a.score);

      if (headerScores.length > 0 && headerScores[0].score >= 2) {
        return headerScores[0].index;
      }

      // Ultimate fallback to first row
      return 0;
    },

// Enhanced detectHeaderRowExcel method for Excel files
    detectHeaderRowExcel(rows) {
      if (rows.length < 2) return 0; // Not enough data

      // More comprehensive header patterns
      const headerPatterns = [
        /receipt.?no/i, /transaction.?id/i, /date/i, /time/i,
        /amount/i, /balance/i, /reference/i, /description/i,
        /account/i, /payment/i, /withdrawal/i, /deposit/i,
        /paid.?in/i, /withdrawn/i, /status/i, /details/i,
        /completion/i, /initiation/i, /party/i, /reason/i,
        /narration/i, /mpesa/i, /message/i, /sender/i, /recipient/i,
        /debit/i, /credit/i, /transfer/i, /bank/i, /branch/i,
        /remarks/i, /type/i, /category/i, /currency/i, /fee/i,
        /statement/i, /particulars/i, /charges/i, /serial/i
      ];

      // Statistical analysis approach
      const rowAnalysis = [];

      // Analyze each row for header-like characteristics
      for (let i = 0; i < Math.min(rows.length, 20); i++) {
        const row = rows[i];

        // Skip empty rows or rows with too few cells
        if (!row || row.length < 3) continue;

        // Initialize score
        let headerScore = 0;

        // Count cells that match header patterns
        const headerMatches = row.filter(cell =>
            cell && typeof cell === 'string' &&
            headerPatterns.some(pattern => pattern.test(cell))
        ).length;

        headerScore += headerMatches * 2;

        // Check content types
        const textCells = row.filter(cell =>
            cell && typeof cell === 'string' &&
            cell.trim() !== '' &&
            isNaN(cell.trim())
        ).length;

        const numericCells = row.filter(cell =>
            cell &&
            (typeof cell === 'number' ||
                (typeof cell === 'string' && !isNaN(cell.trim()) && cell.trim() !== ''))
        ).length;

        // Headers typically have more text than numbers
        if (textCells > numericCells) {
          headerScore += 2;
        }

        // Check formatting consistency in the row
        // Headers often have consistent formatting
        const consistentFormatting = this.checkConsistentFormatting(row);
        headerScore += consistentFormatting ? 1 : 0;

        // Check if next row exists and has consistent data types
        if (i < rows.length - 1) {
          const nextRow = rows[i + 1];

          if (nextRow && nextRow.length === row.length) {
            // Check if next row has consistent data types different from current row
            // (headers followed by data often show this pattern)
            const differentTypes = this.checkDifferentTypes(row, nextRow);
            headerScore += differentTypes ? 2 : 0;
          }
        }

        // Store analysis
        rowAnalysis.push({
          index: i,
          score: headerScore,
          cellCount: row.length,
        });
      }

      // Sort by score (highest first)
      rowAnalysis.sort((a, b) => b.score - a.score);

      // Choose the highest scoring row if it's score is good enough
      if (rowAnalysis.length > 0 && rowAnalysis[0].score >= 4) {
        const candidateRow = rowAnalysis[0].index;

        // Additional verification: check if following rows have consistent structure
        if (candidateRow < rows.length - 1) {
          const headerRow = rows[candidateRow];
          let consistentStructureCount = 0;

          // Check up to 5 rows after the candidate header
          for (let j = 1; j <= Math.min(5, rows.length - candidateRow - 1); j++) {
            const dataRow = rows[candidateRow + j];

            if (dataRow && dataRow.length === headerRow.length) {
              consistentStructureCount++;
            }
          }

          // If at least 2 following rows have same structure, this is likely the header
          if (consistentStructureCount >= 2) {
            return candidateRow;
          }
        }
      }

      // Fallback to first row if no clear header is detected
      return 0;
    },

// Helper method to check formatting consistency in a row
    checkConsistentFormatting(row) {
      if (!row || row.length < 3) return false;

      // Check for consistent capitalization
      const capitalizationTypes = {
        uppercase: 0,
        lowercase: 0,
        titlecase: 0,
        mixed: 0
      };

      row.forEach(cell => {
        if (!cell || typeof cell !== 'string' || cell.trim() === '') return;

        const text = cell.trim();

        if (text === text.toUpperCase()) {
          capitalizationTypes.uppercase++;
        } else if (text === text.toLowerCase()) {
          capitalizationTypes.lowercase++;
        } else if (
            text.split(/\s+/).every(word =>
                word.length > 0 &&
                word[0] === word[0].toUpperCase() &&
                word.slice(1).toLowerCase() === word.slice(1)
            )
        ) {
          capitalizationTypes.titlecase++;
        } else {
          capitalizationTypes.mixed++;
        }
      });

      // Get the dominant formatting
      const dominant = Object.entries(capitalizationTypes)
          .sort((a, b) => b[1] - a[1])[0];

      // If dominant formatting applies to at least 70% of cells, consider it consistent
      return dominant[1] / row.length >= 0.7;
    },

// Helper method to check if two rows have different data types
    checkDifferentTypes(row1, row2) {
      if (!row1 || !row2 || row1.length !== row2.length || row1.length < 3) {
        return false;
      }

      let differentTypeCount = 0;

      for (let i = 0; i < row1.length; i++) {
        const cell1 = row1[i];
        const cell2 = row2[i];

        // If either cell is empty/null, skip
        if (!cell1 || !cell2) continue;

        const type1 = typeof cell1 === 'number' ||
        (typeof cell1 === 'string' && !isNaN(cell1.trim()) && cell1.trim() !== '')
            ? 'number' : 'text';

        const type2 = typeof cell2 === 'number' ||
        (typeof cell2 === 'string' && !isNaN(cell2.trim()) && cell2.trim() !== '')
            ? 'number' : 'text';

        if (type1 !== type2) {
          differentTypeCount++;
        }
      }

      // If at least 30% of columns have different types, return true
      return differentTypeCount / row1.length >= 0.3;
    },

// Enhanced method to detect exact date format in CSV
    parseCSV(content) {
      // First, try to identify metadata and headers
      const lines = content.split('\n');

      // Check for common file encoding issues and clean if necessary
      const cleanedLines = lines.map(line =>
          // Handle BOM and other encoding artifacts
          line.replace(/^\ufeff/, '').replace(/\r$/, '')
      );

      // Skip completely empty lines
      const nonEmptyLines = cleanedLines.filter(line => line.trim() !== '');

      // Try to detect the header row
      const headerRowIndex = this.detectHeaderRow(nonEmptyLines);

      let metadataLines = [];
      let dataLines = [];

      if (headerRowIndex > 0) {
        // Extract metadata (lines before header)
        metadataLines = nonEmptyLines.slice(0, headerRowIndex);

        // Extract header and data rows
        const headerLine = nonEmptyLines[headerRowIndex];
        dataLines = nonEmptyLines.slice(headerRowIndex + 1);

        // Store metadata for display
        this.extractMetadata(metadataLines);

        // Prepare CSV with just header and data
        const filteredCSV = [headerLine, ...dataLines].join('\n');

        // Parse the filtered CSV
        Papa.parse(filteredCSV, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          transformHeader: (header) => {
            // Clean up headers by removing extra spaces and quotes
            return header.trim().replace(/^["'](.*)["']$/, '$1');
          },
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              // Clean empty headers and filter out empty columns
              const cleanedData = this.cleanupParsedData(results.data);
              const cleanedHeaders = Object.keys(cleanedData[0] || {}).filter(
                  header => header !== null && header !== undefined && header.trim() !== ''
              );

              this.processParseResults(cleanedData, cleanedHeaders);
              this.headerRowIndex = headerRowIndex;
            } else {
              this.showError('No data found in the CSV file after processing headers.');
            }
          },
          error: (error) => {
            this.showError('Error parsing CSV: ' + error.message);
          }
        });
      } else {
        // Standard parsing if no clear header detected
        Papa.parse(content, {
          header: true,
          skipEmptyLines: true,
          dynamicTyping: true,
          transformHeader: (header) => {
            return header.trim().replace(/^["'](.*)["']$/, '$1');
          },
          complete: (results) => {
            if (results.data && results.data.length > 0) {
              const cleanedData = this.cleanupParsedData(results.data);
              const cleanedHeaders = Object.keys(cleanedData[0] || {}).filter(
                  header => header !== null && header !== undefined && header.trim() !== ''
              );

              this.processParseResults(cleanedData, cleanedHeaders);
              this.headerRowIndex = 0;
            } else {
              this.showError('No data found in the CSV file.');
            }
          },
          error: (error) => {
            this.showError('Error parsing CSV: ' + error.message);
          }
        });
      }
    },

// Helper method to clean up parsed data
    cleanupParsedData(data) {
      // Filter out rows that are all empty or null
      const nonEmptyRows = data.filter(row => {
        return Object.values(row).some(value =>
            value !== null && value !== undefined && String(value).trim() !== ''
        );
      });

      // Remove columns that have empty headers or are all empty
      if (nonEmptyRows.length > 0) {
        const firstRow = nonEmptyRows[0];
        const emptyHeaders = Object.keys(firstRow).filter(header =>
            header === null || header === undefined || header.trim() === ''
        );

        // Remove empty header columns from all rows
        return nonEmptyRows.map(row => {
          const cleanRow = { ...row };
          emptyHeaders.forEach(header => delete cleanRow[header]);
          return cleanRow;
        });
      }

      return nonEmptyRows;
    },

// Enhanced Excel parsing with better header detection
    parseExcel(content) {
      try {
        const data = new Uint8Array(content);
        const workbook = XLSX.read(data, {
          type: 'array',
          cellDates: true,
          cellNF: true,
          cellText: false
        });

        // Get the first sheet
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];

        // Process sheet options
        const sheetOptions = worksheet['!ref'] ?
            XLSX.utils.decode_range(worksheet['!ref']) : { s: { r: 0, c: 0 }, e: { r: 0, c: 0 } };

        // Extract all rows as arrays for header detection
        const sheetRows = XLSX.utils.sheet_to_json(worksheet, {
          header: 1,
          defval: null,
          range: sheetOptions
        });

        // Detect the header row
        const headerRowIndex = this.detectHeaderRowExcel(sheetRows);

        if (headerRowIndex > 0) {
          // Extract metadata before header row
          const metadataRows = sheetRows.slice(0, headerRowIndex);
          this.extractMetadata(metadataRows);

          // Get header row and data rows
          const headerRow = sheetRows[headerRowIndex];

          // Create a new range starting from the header row
          const newRange = {
            s: { r: 0, c: 0 },
            e: {
              r: Math.max(0, sheetRows.length - headerRowIndex - 1),
              c: headerRow ? headerRow.length - 1 : 0
            }
          };

          // Create a new worksheet with just headers and data
          const newWorksheet = {};

          // Set the range reference
          newWorksheet['!ref'] = XLSX.utils.encode_range(newRange);

          // Copy header row (becomes row 0 in new sheet)
          if (headerRow) {
            for (let c = 0; c <= newRange.e.c; c++) {
              if (c < headerRow.length && headerRow[c] !== null && headerRow[c] !== undefined) {
                const cellAddress = XLSX.utils.encode_cell({ r: 0, c: c });

                // Create the cell if it exists in the original header
                const originalCellAddress = XLSX.utils.encode_cell({ r: headerRowIndex, c: c });

                if (worksheet[originalCellAddress]) {
                  newWorksheet[cellAddress] = { ...worksheet[originalCellAddress] };
                } else {
                  // Create a new cell if original doesn't exist but we have a value
                  newWorksheet[cellAddress] = { t: 's', v: headerRow[c].toString() };
                }
              }
            }
          }

          // Copy data rows (after header row in original sheet)
          for (let r = 1; r <= newRange.e.r; r++) {
            const originalRowIndex = headerRowIndex + r;

            if (originalRowIndex < sheetRows.length) {
              const dataRow = sheetRows[originalRowIndex];

              for (let c = 0; c <= newRange.e.c; c++) {
                if (c < dataRow.length && dataRow[c] !== null && dataRow[c] !== undefined) {
                  const cellAddress = XLSX.utils.encode_cell({ r: r, c: c });
                  const originalCellAddress = XLSX.utils.encode_cell({ r: originalRowIndex, c: c });

                  if (worksheet[originalCellAddress]) {
                    newWorksheet[cellAddress] = { ...worksheet[originalCellAddress] };
                  } else {
                    // Handle the case where cell might not exist in original but we have a value
                    const value = dataRow[c];
                    const cellType = typeof value === 'number' ? 'n' :
                        value instanceof Date ? 'd' : 's';

                    newWorksheet[cellAddress] = { t: cellType, v: value };
                  }
                }
              }
            }
          }

          // Convert modified worksheet to JSON
          const jsonData = XLSX.utils.sheet_to_json(newWorksheet, {
            defval: null,
            raw: false  // Convert dates to JS date objects
          });

          if (jsonData.length > 0) {
            // Clean up headers and data
            const cleanedData = jsonData.map(row => {
              const cleanRow = {};

              Object.entries(row).forEach(([key, value]) => {
                // Clean header
                const cleanKey = key.trim();

                if (cleanKey !== '') {
                  cleanRow[cleanKey] = value;
                }
              });

              return cleanRow;
            });

            const cleanedHeaders = Object.keys(cleanedData[0] || {});

            this.processParseResults(cleanedData, cleanedHeaders);
            this.headerRowIndex = headerRowIndex;
          } else {
            this.showError('No data found in the Excel file after processing headers.');
          }
        } else {
          // Use standard conversion if no clear header row detected
          const jsonData = XLSX.utils.sheet_to_json(worksheet, {
            defval: null,
            raw: false  // Convert dates to JS date objects
          });

          if (jsonData.length > 0) {
            // Clean up data
            const cleanedData = jsonData.map(row => {
              const cleanRow = {};

              Object.entries(row).forEach(([key, value]) => {
                // Clean header
                const cleanKey = key.trim();

                if (cleanKey !== '') {
                  cleanRow[cleanKey] = value;
                }
              });

              return cleanRow;
            });

            const cleanedHeaders = Object.keys(cleanedData[0] || {});

            this.processParseResults(cleanedData, cleanedHeaders);
            this.headerRowIndex = 0;
          } else {
            this.showError('No data found in the Excel file.');
          }
        }
      } catch (error) {
        this.showError('Error parsing Excel file: ' + error.message);
        console.error('Excel parsing error:', error);
      }
    }
  }
};
</script>

<style scoped>
/* Modal styles */
.modal-lg {
  max-width: 1000px;
}

/* Step indicator styles */
.steps-container {
  position: relative;
  margin-bottom: 2rem;
}

.step-progress {
  position: absolute;
  top: 24px;
  left: 0;
  width: 100%;
  height: 2px;
  background-color: #e9ecef;
  z-index: 1;
}

.step-progress-bar {
  height: 100%;
  background-color: #007bff;
  transition: width 0.3s ease;
}

.steps {
  display: flex;
  justify-content: space-between;
  position: relative;
  z-index: 2;
}

.step {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6c757d;
}

.step.active {
  color: #007bff;
}

.step-icon {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background-color: #fff;
  border: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.5rem;
  font-size: 1.5rem;
}

.step.active .step-icon {
  border-color: #007bff;
  color: #007bff;
}

.step-label {
  font-weight: 600;
}

/* File upload styles */
.upload-container {
  margin-bottom: 1.5rem;
}

.upload-dropzone {
  border: 2px dashed #dee2e6;
  border-radius: 0.5rem;
  padding: 2rem;
  text-align: center;
  background-color: #f8f9fa;
  transition: all 0.2s ease;
}

.upload-dropzone.is-dragover {
  border-color: #007bff;
  background-color: rgba(0, 123, 255, 0.05);
}

.upload-dropzone.has-file {
  border-color: #28a745;
  background-color: rgba(40, 167, 69, 0.05);
}

.file-input {
  position: absolute;
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  z-index: -1;
}

.upload-prompt {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.upload-icon {
  font-size: 3rem;
  color: #6c757d;
  margin-bottom: 1rem;
}

.btn-choose-file {
  padding: 0.5rem 1.5rem;
}

.supported-files {
  color: #6c757d;
  font-size: 0.875rem;
}

.selected-file-container {
  display: flex;
  align-items: center;
  padding: 1rem;
  background-color: #fff;
  border-radius: 0.25rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.file-icon {
  font-size: 2rem;
  margin-right: 1rem;
  color: #007bff;
}

.file-details {
  flex: 1;
}

.file-name {
  margin-bottom: 0.25rem;
  font-weight: 600;
}

.file-size {
  margin-bottom: 0;
  color: #6c757d;
  font-size: 0.875rem;
}

/* Table styles */
.table-container {
  max-height: 400px;
  overflow-y: auto;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
}

.sticky-header {
  position: sticky;
  top: 0;
  z-index: 1;
}

.sortable-header {
  cursor: pointer;
  white-space: nowrap;
}

.sortable-header:hover {
  background-color: rgba(0, 0, 0, 0.075);
}

/* Search styles */
.search-container {
  position: relative;
}

/* Pagination styles */
.pagination-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1rem;
}

.records-info {
  color: #6c757d;
}

.filtered-text {
  font-style: italic;
}

.pagination-controls {
  display: flex;
  align-items: center;
}

.items-per-page {
  display: flex;
  align-items: center;
}

.page-info {
  margin: 0 0.5rem;
  display: flex;
  align-items: center;
}

.page-input {
  width: 50px;
  text-align: center;
  margin-right: 0.25rem;
}

/* Loading styles */
.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.batch-stats {
  background-color: #f8f9fa;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 0.75rem 1rem;
}

.spinner {
  margin-bottom: 1rem;
}

.loading-message {
  text-align: center;
  color: #6c757d;
}

/* Empty state */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #6c757d;
}

.empty-state i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

/* Date filter styles */
.date-filter-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  padding: 1rem;
  background-color: #f8f9fa;
}

.date-filter-header {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.date-range-inputs {
  display: flex;
  align-items: flex-end;
  flex-wrap: wrap;
  gap: 1rem;
}

.date-input-group {
  flex: 1;
  min-width: 150px;
}

.date-range-actions {
  display: flex;
  align-items: center;
}

/* Metadata styles */
.metadata-container {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  overflow: hidden;
}

.metadata-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  font-weight: 600;
}

.metadata-content {
  padding: 1rem;
  background-color: #fff;
  max-height: 200px;
  overflow-y: auto;
}

.metadata-line {
  margin-bottom: 0.5rem;
  font-size: 0.875rem;
}

.metadata-info {
  margin-top: 0.75rem;
}

/* Summary stats */
.data-summary {
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
}

.summary-item {
  flex: 1;
  min-width: 200px;
  display: flex;
  align-items: center;
  padding: 1rem;
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  background-color: #fff;
}

.summary-icon {
  font-size: 1.5rem;
  color: #007bff;
  margin-right: 1rem;
}

.summary-content {
  flex: 1;
}

.summary-content h6 {
  margin-bottom: 0.25rem;
  color: #6c757d;
}

.summary-value {
  font-weight: 600;
  font-size: 1.1rem;
}

/* Alert styles */
.alert-container {
  position: relative;
}

.alert {
  margin-bottom: 0;
}
</style>